import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Input,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import theme from '../../styles/theme';
import { obtenerColorResultado } from '../../utils/UtilesComun';

const commonStyle = {
  padding: theme.spacing(1),
  margin: theme.spacing(0.25),
  borderRadius: theme.borderRadius,
  border: `0.5px solid ${theme.palette.primary.main}`,
  textAlign: 'center',
  cursor: 'pointer',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.custom.botones,
};

/** ----------------------------------------------------------------------------------------------------------
 *
 * COMPONENTES DE STOCK
 *
 * ----------------------------------------------------------------------------------------------------------
 */

/**
 * Alert
 */
export const AlertApp = styled(Alert)(({ severity }) => ({
  ...commonStyle,
  '& .MuiAlert-message': {
    fontSize: theme.typography.text.fontSize,
  },
  '& .MuiAlert-icon': {
    opacity: 1,
    color: severity === 'error' ? theme.palette.error.main : theme.palette.primary.main,
  },
}));

/**
 * Autocomplete
 */
export const AutocompleteApp = styled(Autocomplete, {
  shouldForwardProp: (prop) => prop !== 'noBorder',
})(({ noBorder = false }) => ({
  ...commonStyle,
  border: noBorder ? 'none' : `0.5px solid ${theme.palette.primary.main}`,
  fontSize: theme.typography.text.fontSize,
  '& .MuiAutocomplete-inputRoot': {
    padding: theme.spacing(0),
  },
  '& > *': {
    padding: theme.spacing(1),
  },
}));

/**
 * Avatar de usuario con iniciales.
 */
export const AvatarApp = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'esPropio' && prop !== 'size',
})(({ theme, esPropio = true, size = 28 }) => ({
  width: size,
  height: size,
  fontSize: size * 0.4,
  backgroundColor: esPropio ? theme.palette.primary.main : theme.palette.secondary?.main || '#6b7280',
}));

/**
 * Button
 */
export const ButtonApp = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'contrast',
})(({ contrast }) => ({
  ...commonStyle,
  backgroundColor: contrast ? theme.palette.primary.main : theme.palette.custom.botones,
  color: contrast ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
  },
  '& > *': {
    color: contrast ? theme.palette.primary.contrastText : commonStyle.color,
  },
}));

/**
 * Container
 */
export const ContainerApp = styled(Container)(() => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  maxWidth: false,
}));

/**
 * Icon
 */
export const IconApp = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'fontSize',
})(({ fontSize }) => ({
  fontSize: fontSize || theme.typography.text.fontSize,
  '& > *': {
    fontSize: fontSize || theme.typography.text.fontSize,
  },
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
  },
}));

export const IconButtonApp = styled(IconButton, {
  shouldForwardProp: (prop) => !['enLista', 'backgroundColor', 'border', 'noBorder'].includes(prop),
})(({ enLista, backgroundColor, border = false }) => ({
  width: enLista ? theme.spacing(1.5) : theme.spacing(4),
  height: enLista ? theme.spacing(1.5) : theme.spacing(4),
  padding: theme.spacing(1.25),
  backgroundColor,
  border: border ? `1px solid ${theme.palette.text.secondary}` : '1px solid transparent',
  cursor: 'pointer',
  '& > *': {
    fontSize: enLista ? theme.typography.text.fontSize : theme.typography.h1.fontSize,
  },
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
  },
}));

/**
 * Input
 */
export const InputApp = styled(Input, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ width }) => ({
  ...commonStyle,
  height: theme.spacing(4.5),
  width: width || theme.spacing(10),
  fontSize: theme.typography.text.fontSize,
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
  },
}));

/**
 * Paper
 */
export const PaperApp = styled(Paper)(() => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  border: `0.5px solid ${theme.palette.primary.main}`,
}));

/**
 * Select
 */
export const SelectApp = styled(Select, {
  shouldForwardProp: (prop) => !['width', 'height', 'handleOverflow'].includes(prop),
})(({ width, height, handleOverflow = true }) => ({
  ...commonStyle,
  border: 'none',
  width: width ? theme.spacing(width) : theme.spacing(20),
  height: height ? theme.spacing(height) : theme.spacing(5),
  fontSize: theme.typography.text.fontSize,
  overflow: handleOverflow ? 'hidden' : 'visible',
  textOverflow: handleOverflow ? 'ellipsis' : 'clip',
  whiteSpace: handleOverflow ? 'nowrap' : 'normal',
  '& .MuiSelect-select': {
    fontSize: theme.typography.text.fontSize,
  },
  '& .MuiSelect-icon': {
    fontSize: theme.typography.h1.fontSize,
  },
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
  },
}));

/**
 * Table
 */
export const TableApp = styled(Table)(() => ({
  fontSize: theme.typography.text.fontSize,
  tableLayout: 'fixed',
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.custom.tablasSuave,
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: theme.typography.textSmall.fontSize,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableCell-body': {
    fontSize: theme.typography.text.fontSize,
    color: theme.palette.text.primary,
  },
}));

/**
 * TablePagination
 */
export const TablePaginationApp = styled(TablePagination)(() => ({
  overflow: 'hidden',
  '.MuiTablePagination-toolbar': {
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(-1.5),
    marginRight: theme.spacing(1),
  },
  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiInputBase-root, .MuiTablePagination-select':
    {
      fontSize: theme.typography.textSmall.fontSize,
      fontFamily: theme.typography.fontFamily,
    },
}));

/**
 * TextField
 */
export const TextFieldApp = styled(TextField, {
  shouldForwardProp: (prop) => !['border', 'noBorder'].includes(prop),
})(({ border = true, noBorder = false }) => ({
  ...commonStyle,
  backgroundColor: 'transparent',
  border: border && !noBorder ? `0.5px solid ${theme.palette.primary.main}` : 'none',
  padding: theme.spacing(1),
  fontSize: theme.typography.text.fontSize,
  label: {
    fontSize: theme.typography.text.fontSize,
    padding: theme.spacing(0.5),
  },
}));

/**
 * Typography
 */
export const TypographyApp = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'handleOverflow',
})(({ fontSize, handleOverflow }) => ({
  textTransform: 'none',
  fontSize: fontSize || theme.typography.textSmall.fontSize,
  color: theme.palette.text.primary,
  overflow: handleOverflow ? 'hidden' : 'visible',
  textOverflow: handleOverflow ? 'ellipsis' : 'clip',
  whiteSpace: handleOverflow ? 'nowrap' : 'normal',
}));

/** ----------------------------------------------------------------------------------------------------------
 *
 * COMPONENTES PERSONALIZADOS
 *
 * ----------------------------------------------------------------------------------------------------------
 */

/** -----------------
 * HEADERS
 * ------------------
 */

/**
 * Header de las paginas
 */
export const PageHeaderBox = styled(Box)(() => ({
  padding: theme.spacing(1),
  fontSize: theme.typography.h1.fontSize,
  fontWeight: theme.typography.h1.fontWeight,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

/**
 * Header de los componentes
 */
const BoxTitleAppRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'conFondo',
})(({ conFondo = false }) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.primary,
  backgroundColor: conFondo ? theme.palette.custom.tablas : 'transparent',
}));

export const BoxTitleApp = ({ children, action, conFondo = false, sx = {}, ...props }) => {
  if (action) {
    return (
      <BoxTitleAppRoot
        conFondo={conFondo}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 40,
          mb: 1.5,
          ...sx,
        }}
        {...props}
      >
        <Box sx={{ width: '100%', textAlign: 'center' }}>{children}</Box>
        <Box sx={{ position: 'absolute', right: 12, display: 'flex', alignItems: 'center' }}>{action}</Box>
      </BoxTitleAppRoot>
    );
  }
  return (
    <BoxTitleAppRoot conFondo={conFondo} sx={sx} {...props}>
      {children}
    </BoxTitleAppRoot>
  );
};

/**
 * Pantalla / indicador de carga localizado
 */
export const LoadingApp = ({ minHeight = '50vh', py = 6 }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight, py, width: '100%' }}>
      <CircularProgress sx={{ color: theme.palette.primary.main }} />
    </Box>
  );
};

/** -----------------
 * MENUS
 * ------------------
 */

/**
 * Texto de un item de menu
 */
export const MenuItemTypographyApp = ({ children, ...props }) => {
  return (
    <MenuItem {...props}>
      <TypographyApp handleOverflow>{children}</TypographyApp>
    </MenuItem>
  );
};

/** -----------------
 * LISTAS
 * ------------------
 */

/**
 * Item de texto con primary y secondary
 */
export const ListItemTextApp = ({ primary, secondary }) => {
  return (
    <ListItemText
      primary={<TypographyApp fontSize={theme.typography.text.fontSize}>{primary}</TypographyApp>}
      secondary={
        secondary ? (
          <TypographyApp fontSize={theme.typography.textSmall.fontSize} sx={{ color: 'text.secondary' }}>
            {secondary}
          </TypographyApp>
        ) : undefined
      }
    />
  );
};

/**
 * Botón en un item de lista, con icono y texto
 */
export const ListItemButtonApp = ({ onClick, item, children }) => {
  return (
    <ListItemButton sx={{ width: '100%', height: 40, minHeight: 40, maxHeight: 40, px: 2, mr: 2 }} onClick={onClick}>
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemTextApp primary={item.text} />
      {children}
    </ListItemButton>
  );
};

/** -----------------
 * GENERAL
 * ------------------
 */

/**
 * Box a modo de botón
 */
export const BoxButtonApp = styled(Box)(() => ({
  ...commonStyle,
  backgroundColor: theme.palette.custom.tablas,
  padding: theme.spacing(1.5, 3),
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontWeight: theme.typography.h4.fontWeight,
  fontSize: '0.85rem',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * Box contenedor vertical para secciones de datos
 */
/**
 * Box contenedor vertical para secciones de datos
 */
export const BoxDatosApp = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'resaltar',
})(({ resaltar = false }) => ({
  borderRadius: theme.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: resaltar ? theme.palette.custom.tablas : theme.palette.custom.tablasSuave,
  border: `0.75px solid ${theme.palette.primary.main}`,
}));

/**
 * Box de item de datos
 */
export const BoxDatosItemApp = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'espaciado',
})(({ espaciado = false }) => ({
  backgroundColor: espaciado ? 'white' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderRadius: theme.spacing(2),
  padding: espaciado ? theme.spacing(2) : 0,
  boxShadow: espaciado ? theme.shadows[2] : 'none',
  margin: espaciado ? theme.spacing(0.5) : 0,
}));

/**
 * Select que renderiza opciones automaticamente
 */
export const SelectWrapperApp = ({ options, optionValue, renderLabel = (opt) => opt, onItemClick, ...props }) => {
  return (
    <SelectApp {...props}>
      {options.map((opt) => {
        const value = optionValue ? opt[optionValue] : opt;
        return (
          <MenuItemTypographyApp
            key={value}
            value={value}
            onClick={onItemClick ? (e) => onItemClick(value, e) : undefined}
          >
            {renderLabel(opt)}
          </MenuItemTypographyApp>
        );
      })}
    </SelectApp>
  );
};

/**
 * Select Elegante minimalista para headers, sidebars o barras agrupadoras
 */
export const SelectEleganteApp = ({
  options,
  value,
  onChange,
  placeholder,
  sx = {},
  optionValue,
  renderLabel = (opt) => opt,
  ...props
}) => {
  return (
    <SelectApp
      value={value ?? ''}
      onChange={onChange}
      displayEmpty
      disableUnderline
      variant="standard"
      sx={{
        fontSize: '0.85rem',
        minWidth: 50,
        backgroundColor: 'transparent',
        width: 'auto',
        '& .MuiSelect-select': { py: 0.5, px: 1 },
        '& fieldset': { border: 'none' },
        ...sx,
      }}
      {...props}
    >
      {placeholder && (
        <MenuItem value="" disabled sx={{ fontSize: '0.85rem' }}>
          {placeholder}
        </MenuItem>
      )}
      {options &&
        options.map((option, i) => {
          const val = optionValue ? option[optionValue] : option;
          return (
            <MenuItem key={i} value={val} sx={{ fontSize: '0.85rem' }}>
              {renderLabel(option)}
            </MenuItem>
          );
        })}
    </SelectApp>
  );
};

/**
 * Icono de visibilidad (Publica, Privada, Compartida)
 */
export const VisibilidadIconApp = React.forwardRef(({ visibilidad, sx = {}, ...props }, ref) => {
  const commonSx = { fontSize: '1.0rem', ...sx };
  switch (visibilidad?.toUpperCase()) {
    case 'PUBLICA':
      return <PublicIcon ref={ref} sx={commonSx} {...props} />;
    case 'COMPARTIDA':
      return <GroupIcon ref={ref} sx={commonSx} {...props} />;
    case 'PRIVADA':
    default:
      return <LockIcon ref={ref} sx={commonSx} {...props} />;
  }
});

VisibilidadIconApp.displayName = 'VisibilidadIconApp';

/**
 * Badge o etiqueta informativa para items
 */
export const BadgeInfoApp = ({ children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: 'rgba(0,0,0,0.06)',
      color: 'text.secondary',
      fontSize: '0.65rem',
      fontWeight: 'bold',
      padding: '2px 6px',
      borderRadius: '4px',
      marginRight: '8px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '18px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * TextField con botones de confirmar y cancelar
 */
export const TextFieldEditApp = ({ value, onChange, onConfirm, onCancel, placeholder, autoFocus = true, sx = {} }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onConfirm();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      <TextFieldApp
        size="small"
        autoFocus={autoFocus}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        sx={{ flex: 1 }}
      />
      <IconButtonApp noBorder onClick={onConfirm}>
        <CheckIcon sx={{ color: 'green' }} />
      </IconButtonApp>
      <IconButtonApp noBorder onClick={onCancel}>
        <CloseIcon sx={{ color: 'red' }} />
      </IconButtonApp>
    </Box>
  );
};

export const TypographyContrastApp = ({ children, ...props }) => {
  return (
    <TypographyApp {...props} sx={{ color: 'primary.contrastText' }}>
      {children}
    </TypographyApp>
  );
};

/** -----------------
 * TABLAS
 * ------------------
 */

/**
 * Celda de una tabla
 */
export const TableCellApp = ({ children, ...props }) => {
  return <TableCell {...props}>{children}</TableCell>;
};

/**
 * Botón con texto en una tabla
 */
export const ButtonTableApp = ({ onClick, text, tooltip, sx = {} }) => {
  const content = (
    <Button
      sx={{
        py: 0,
        px: 0.5,
        minWidth: 0,
        width: '100%',
        justifyContent: 'flex-start',
        ...sx,
      }}
      onClick={onClick}
    >
      <TypographyApp sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</TypographyApp>
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

/**
 * Componente interactivo tipo "pill" o "card" que evidencia interacción sin parecer un botón estridente.
 */
export const InteractiveCardApp = ({ onClick, children, sx = {} }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1,
        p: 0.75,
        transition: 'all 0.2s',
        ...sx,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          borderColor: 'primary.main',
          ...(sx['&:hover'] || {}),
        },
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Select en una tabla que renderiza opciones automaticamente
 */
const StyledSelectTable = styled(Select)(({ theme }) => ({
  minWidth: theme.spacing(10),
  height: 24,
  fontSize: '0.75rem',
  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center',
    height: 24,
  },
}));

export const SelectTableApp = ({ options, optionValue, renderLabel = (opt) => opt, sx, ...props }) => {
  return (
    <StyledSelectTable sx={sx} {...props}>
      {options.map((opt) => {
        const value = optionValue ? opt[optionValue] : opt;
        return (
          <MenuItemTypographyApp key={value} value={value}>
            {renderLabel(opt)}
          </MenuItemTypographyApp>
        );
      })}
    </StyledSelectTable>
  );
};

/**
 * Texto en la celda de una tabla
 */
export const TypographyTableApp = ({ children, handleOverflow, align, sx, ...props }) => {
  const textAlign = align || sx?.textAlign;
  return (
    <TableCell
      align={align}
      sx={{ textAlign: textAlign ? `${textAlign} !important` : undefined, ...sx }}
      overflow="hidden"
      {...props}
    >
      <TypographyApp
        handleOverflow={handleOverflow}
        sx={{
          textAlign: textAlign ? `${textAlign} !important` : 'inherit',
          width: '100%',
          display: 'block',
        }}
      >
        {children}
      </TypographyApp>
    </TableCell>
  );
};

/**
 * Celda con tooltip en una tabla
 */
const TableCellTooltipApp = ({ children, title, align, sx, width, ...props }) => {
  const textAlign = align || sx?.textAlign;
  return (
    <TableCell
      align={align}
      sx={{
        width: width || sx?.width,
        textAlign: textAlign ? `${textAlign} !important` : undefined,
        ...sx,
      }}
      {...props}
    >
      <Tooltip title={title || children} placement="top" arrow>
        {typeof children === 'string' ? (
          <TypographyApp
            sx={{
              textAlign: textAlign ? `${textAlign} !important` : 'inherit',
              width: '100%',
              display: 'block',
            }}
          >
            {children}
          </TypographyApp>
        ) : (
          children
        )}
      </Tooltip>
    </TableCell>
  );
};

/**
 * Celda con tooltip y ordenable en una tabla
 */
const TableCellSortableTooltipApp = ({ children, active, direction, title, align, sx, width, ...props }) => {
  const textAlign = align || sx?.textAlign;
  const isCenter = textAlign === 'center' || align === 'center';
  const isRight = textAlign === 'right' || align === 'right';
  return (
    <TableCellTooltipApp align={align} width={width} sx={sx} {...props} title={title || children}>
      <TableSortLabel
        active={active}
        direction={direction}
        hideSortIcon={true}
        IconComponent={() => null}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: isRight ? 'flex-end' : isCenter ? 'center' : 'flex-start',
          cursor: 'pointer',
          '& .MuiTableSortLabel-icon': {
            display: 'none !important',
          },
        }}
      >
        {typeof children === 'string' ? (
          <TypographyApp
            sx={{
              textAlign: isCenter ? 'center' : isRight ? 'right' : 'inherit',
              width: '100%',
              display: 'block',
            }}
          >
            {children}
          </TypographyApp>
        ) : (
          children
        )}
      </TableSortLabel>
    </TableCellTooltipApp>
  );
};

/**
 * Cabecera de una tabla con columnas ordenables
 */
export const TableHeadSortableApp = ({ columns, sort, onSort, setSort, noHeader }) => {
  const handleSort = (field) => {
    if (setSort) {
      setSort((prev) => ({
        field,
        direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    }
    if (onSort) {
      onSort(field);
    }
  };

  return (
    <TableHead>
      <TableRow sx={{ visibility: noHeader ? 'collapse' : 'visible' }}>
        {columns.map((col) => {
          const isSortable = col.sortable !== false;
          const content = col.icon ? (
            <col.icon sx={{ ...estiloIconoTableHead, color: col.color || 'inherit' }} />
          ) : (
            col.label
          );

          if (isSortable) {
            return (
              <TableCellSortableTooltipApp
                key={col.field}
                width={col.width}
                title={col.tooltip}
                active={sort?.field === col.field}
                direction={sort?.direction || 'desc'}
                onClick={() => handleSort(col.field)}
                align={col.align}
                sx={col.sx}
              >
                {content}
              </TableCellSortableTooltipApp>
            );
          }

          return (
            <TableCellTooltipApp key={col.field} width={col.width} title={col.tooltip} align={col.align} sx={col.sx}>
              {content}
            </TableCellTooltipApp>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

/**
 * Componente modular que envuelve una tabla completa con titulo, cabecera ordanable y paginacion.
 */
export const TableModuleApp = ({
  title,
  headerContent,
  columns,
  noHeader,
  noBorder,
  sort,
  setSort,
  data = [],
  renderRow,
  emptyMessage = 'No hay datos para mostrar.',
  hasPagination = false,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
  rowsPerPageOptions = [10, 25, 50, 100],
  sx = {},
  loading = false,
}) => {
  return (
    <PaperApp
      sx={{
        border: noBorder ? 'none' : `0.5px solid ${theme.palette.primary.main}`,
        width: '100%',
        overflow: 'hidden',
        padding: 0,
        ...sx,
      }}
    >
      {title && (
        <BoxTitleApp sx={{ fontSize: '0.9rem' }} conFondo>
          {title}
        </BoxTitleApp>
      )}
      {headerContent}
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            minHeight: 200,
            py: 6,
          }}
        >
          <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <TableApp size="small">
              {columns && (
                <colgroup>
                  {columns.map((col) => (
                    <col key={col.field} style={col.width ? { width: col.width } : undefined} />
                  ))}
                </colgroup>
              )}
              <TableHeadSortableApp columns={columns} sort={sort} setSort={setSort} noHeader={noHeader} />
              <TableBody>
                {data.map((item, index) => renderRow(item, index))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCellApp colSpan={columns ? columns.length : 1} sx={{ textAlign: 'center', py: 3 }}>
                      <TypographyApp>{emptyMessage}</TypographyApp>
                    </TableCellApp>
                  </TableRow>
                )}
              </TableBody>
            </TableApp>
          </TableContainer>
          {hasPagination && (
            <TablePaginationApp
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={totalCount || data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontSize: theme.typography.textSmall.fontSize,
                        fontFamily: theme.typography.fontFamily,
                        minHeight: 'auto',
                        py: 0.5,
                      },
                    },
                  },
                },
              }}
            />
          )}
        </>
      )}
    </PaperApp>
  );
};

/** ----------------------------------------------------------------------------------------------------------
 *
 * ESTILOS
 *
 * ----------------------------------------------------------------------------------------------------------
 */

/**
 * Celda de una tabla que representa la categoria de un jugador
 */
export const TableCellCategoria = styled(TypographyTableApp, {
  shouldForwardProp: (prop) => prop !== 'estadoJugador',
})(({ theme, estadoJugador }) => {
  let backgroundColor = 'transparent';

  if (estadoJugador === 3) {
    backgroundColor = theme.palette.categoria.plus3;
  } else if (estadoJugador === 2) {
    backgroundColor = theme.palette.categoria.plus2;
  } else if (estadoJugador === 1) {
    backgroundColor = theme.palette.categoria.plus1;
  } else if (estadoJugador === -1) {
    backgroundColor = theme.palette.categoria.minus1;
  } else if (estadoJugador === -2) {
    backgroundColor = theme.palette.categoria.minus2;
  }

  return {
    backgroundColor,
    textAlign: 'center !important',
    justifyContent: 'center',
    paddingLeft: '2px !important',
    paddingRight: '2px !important',
    '& .MuiTypography-root': {
      textAlign: 'center !important',
    },
  };
});

/**
 * Celda de una tabla que representa el porcentaje de titularidad de un jugador
 */
export const TableCellTitular = styled(TypographyTableApp, {
  shouldForwardProp: (prop) => prop !== 'opacidad',
})(({ opacidad }) => ({
  backgroundColor: `rgba(26, 35, 126, ${(opacidad >= 0 ? opacidad : 0) * 0.2})`,
}));

/**
 * Celda de una tabla que representa la diferencia de goles de un partido
 */
export const TableCellDiferenciaGoles = styled(TypographyTableApp)(({ theme, dg }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiTypography-root': {
    color: dg >= 0 ? theme.palette.text.primary : theme.palette.error.main,
  },
}));

/**
 * Estilo de los iconos de la cabecera de la tabla
 */
export const estiloIconoTableHead = {
  fontSize: '0.82rem',
  verticalAlign: 'middle',
  marginTop: theme.spacing(0),
  paddingTop: theme.spacing(0),
};

/**
 * Estilo de una celda de la tabla de resultados de partidos
 */
export const estiloResultadoPartido = (jugado, esVictoria, esDerrota) => {
  return {
    backgroundColor: !jugado
      ? ''
      : esVictoria
        ? theme.palette.success.light
        : esDerrota
          ? theme.palette.fail.light
          : theme.palette.default.main,
  };
};

/**
 * Estilo de una fila de la tabla de clasificación. Se resalta el equipo seleccionado
 */
export const estiloFilaClasificacion = (resaltado) => {
  return {
    backgroundColor: resaltado ? theme.palette.highlight.main : '',
  };
};

export const estiloBotonTablaJugador = (enAlgunaLista) => {
  return {
    backgroundColor: enAlgunaLista ? theme.palette.success.main : 'transparent',
  };
};

/** -----------------
 * DIALOGOS
 * ------------------
 */

/**
 * Dialog generico de la aplicacion
 */
export const DialogApp = ({ open, onClose, title, children, actions, fullWidth = true, maxWidth = 'md', ...props }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth={fullWidth} maxWidth={maxWidth} {...props}>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions sx={{ justifyContent: 'flex-start', ml: theme.spacing(3) }}>{actions}</DialogActions>}
    </Dialog>
  );
};

/**
 * Dialog con un input de texto y botones de accion
 */
export const DialogInputApp = ({
  open,
  onClose,
  onConfirm,
  title,
  label,
  value,
  onChange,
  multiline = true,
  rows = 10,
  type = 'text',
  confirmText = 'Guardar',
  cancelText = 'Cancelar',
}) => {
  return (
    <DialogApp
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <ButtonApp
            sx={{ '&:hover': { backgroundColor: theme.palette.success.light } }}
            onClick={onConfirm}
            variant="contained"
          >
            {confirmText}
          </ButtonApp>
          <ButtonApp
            sx={{ '&:hover': { backgroundColor: theme.palette.fail.light } }}
            onClick={onClose}
            variant="outlined"
          >
            {cancelText}
          </ButtonApp>
        </>
      }
    >
      <TextFieldApp
        noBorder
        autoFocus
        margin="dense"
        label={label}
        type={type}
        fullWidth
        multiline={multiline}
        rows={rows}
        value={value}
        onChange={onChange}
      />
    </DialogApp>
  );
};

/**
 * Badge de resultado de un partido con paleta temática (Victoria verde, Empate amarillo, Derrota rojo)
 */
export const BadgeResultadoPartidoApp = ({ partido, onClick, tooltip = 'Ver acta', sx = {} }) => {
  const tieneResultado =
    partido &&
    partido.golesLocal !== null &&
    partido.golesLocal !== undefined &&
    partido.golesVisitante !== null &&
    partido.golesVisitante !== undefined;

  const estiloComun = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 54,
    height: 25,
    borderRadius: '4px',
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.15s ease-in-out',
    '&:hover': onClick
      ? {
          filter: 'brightness(0.95)',
          transform: 'scale(1.04)',
        }
      : {},
    ...sx,
  };

  if (!tieneResultado || partido.jugado === false) {
    const emptyContent = (
      <Box
        onClick={onClick}
        sx={{
          ...estiloComun,
          backgroundColor: '#f1f5f9',
          color: '#64748b',
          border: '1px solid #cbd5e1',
        }}
      >
        -
      </Box>
    );
    return tooltip ? (
      <Tooltip title={tooltip} arrow>
        {emptyContent}
      </Tooltip>
    ) : (
      emptyContent
    );
  }

  const golesFavor = partido.esLocal ? partido.golesLocal : partido.golesVisitante;
  const golesContra = partido.esLocal ? partido.golesVisitante : partido.golesLocal;

  const codigoResultado = golesFavor > golesContra ? 'V' : golesFavor === golesContra ? 'E' : 'D';
  const { bg, color, border } = obtenerColorResultado(codigoResultado);

  const content = (
    <Box
      onClick={onClick}
      sx={{
        ...estiloComun,
        backgroundColor: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {partido.golesLocal} - {partido.golesVisitante}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
